import * as Sentry from '@sentry/react';
import { useState, useEffect, useContext } from 'react';
import { Loading, Banner, Card, ResourceList, SkeletonThumbnail, LegacyStack, Thumbnail, Text } from '@shopify/polaris';
import Api from '../helpers/api';
import { Inertia } from '@inertiajs/inertia';

import { CartContext } from '../context-providers/CartContext';
import { personalizationIdPropertyName } from '../globals.js';

export function CartDisplay() {
    const [loading, setLoading] = useState(false);
    const [statusTitle, setStatusTitle] = useState('Initialising...');
    const [statusKind, setStatusKind] = useState('');

    const [productsData, setProductsData] = useState({});
    const [loadedProducts, setLoadedProducts] = useState({});

    const [engravedProductItems, setEngravedProductItems] = useState({});
    const [notEngravedProductItems, setNotEngravedProductItems] = useState({});

    const cartContext = useContext(CartContext);

    useEffect(() => {
        setLoading(true);
        setStatusTitle('Checking cart status...');
    }, []);

    useEffect(() => {
        if (cartContext.cartStatus == 'available') {
            setStatusTitle('Loading cart data...');
            setStatusKind('');
        } else if (cartContext.cartStatus == 'unavailable') {
            setStatusTitle('Cart is not available. Please try again shortly.');
            setStatusKind('critical');
            setLoading(false);
        } else if (cartContext.cartStatus == 'access-error') {
            setStatusTitle('There was an error while fetching the cart. Please try again shortly.');
            setStatusKind('critical');
            setLoading(false);
        } else if (cartContext.cartStatus == 'ready') {
            setStatusTitle('Fetching product info...');
            setStatusKind('');
        }
    }, [cartContext.cartStatus]);

    // Fetch extended product data from the store, for displaying line items
    useEffect(() => {
        if (cartContext.engravingChargeProduct == null) {
            return;
        }
        const fetchLineItemData = async () => {
            if (cartContext.lineItems?.length) {
                let anyItemsHaveData = false;
                setLoadedProducts({});

                cartContext.lineItems.forEach((lineItem) => {
                    if (lineItem.productId == cartContext.engravingChargeProduct.id) {
                        return;
                    }

                    if (lineItem.productId && productsData[lineItem.productId] == undefined) {
                        // Custom line items don't have a productId!
                        setLoadedProducts((existing) => ({
                            ...existing,
                            [lineItem.productId]: false || loadedProducts[lineItem.productId],
                        }));

                        anyItemsHaveData = true;

                        Api.getProduct(lineItem.productId)
                            .then((response) => {
                                let productEntry = {};
                                productEntry[lineItem.productId] = response.data;
                                setProductsData((existing) => ({
                                    ...existing,
                                    [lineItem.productId]: response.data,
                                }));
                            })
                            .catch((error) => {
                                Sentry.captureException(error);
                            })
                            .finally(() => {
                                setLoadedProducts((existing) => ({
                                    ...existing,
                                    [lineItem.productId]: true,
                                }));
                            });
                    }
                });

                if (anyItemsHaveData == false) {
                    setLoading(false);
                }
            } else if (cartContext.lineItems && cartContext.lineItems.length == 0) {
                setLoading(false);
            }
        };
        fetchLineItemData().catch(Sentry.captureException);
    }, [cartContext.lineItems, cartContext.engravingChargeProduct]);

    useEffect(() => {
        if (
            Object.values(loadedProducts).length > 0 &&
            Object.values(loadedProducts).length == Object.values(loadedProducts).filter((val) => val).length
        ) {
            setLoading(false);
        }
    }, [loadedProducts]);

    useEffect(() => {
        if (!cartContext.productLineItems) {
            return;
        }
        // This system allows us to split the line items without losing their original indeces, which are vital for editing properties later on.
        const lineItemsWithProductIndeces =
            cartContext.productLineItems.map((item, productLineItemIndex) => ({
                productId: item.productId,
                title: item.title,
                sku: item.sku,
                quantity: item.quantity,
                variantId: item.variantId,
                properties: item.properties,
                productLineItemIndex,
            })) ?? [];

        setEngravedProductItems(
            lineItemsWithProductIndeces?.filter(
                (item) => item.properties?.filter((property) => property.name == personalizationIdPropertyName).length,
            ),
        );
        setNotEngravedProductItems(
            lineItemsWithProductIndeces?.filter(
                (item) =>
                    !item.properties ||
                    item.properties.filter((property) => property.name == personalizationIdPropertyName).length == 0,
            ),
        );
    }, [cartContext.productLineItems]);

    const DisplayedLineItem = function (item) {
        const { productId, title, sku, quantity, variantId, productLineItemIndex } = item;
        const productData = productsData[productId];

        const variantData = productData?.variants.find((variantData) => variantData.id == variantId);

        const canBeEngraved = productData?.supportsEngraving && variantData?.available_inventory > 0;

        // Image fallbacks - basic skeleton first
        let media = <SkeletonThumbnail size="medium" />;
        // If the product has an image, use that instead
        if (productData?.image?.src) {
            media = <Thumbnail source={productData.image.src} size="medium" />;
        }
        // If the variant has an image, use that instead
        let firstVariantImage = productData?.images.find((imageData) =>
            imageData.variant_ids.some((id) => id == variantId),
        );
        if (firstVariantImage) {
            media = <Thumbnail source={firstVariantImage.src} size="medium" />;
        }

        return (
            <ResourceList.Item
                id={productId}
                onClick={() => {
                    canBeEngraved
                        ? Inertia.visit(`/customize/${productLineItemIndex}/${productId}/${variantId}`)
                        : () => {};
                }}
                media={media}
                accessibilityLabel={`Add engraving for ${title}`}
            >
                <Text
                    variant="bodyMd"
                    fontWeight={canBeEngraved ? 'bold' : ''}
                    as="h3"
                    color={canBeEngraved ? '' : 'subdued'}
                >
                    {title} {variantData && variantData.title != 'Default Title' ? ' - ' + variantData.title : ''}
                </Text>

                {loadedProducts[productId] !== undefined && !loadedProducts[productId] && (
                    <Text color="subdued">Loading...</Text>
                )}

                {productData && !productData.supportsEngraving && <Text color="warning">Engraving not supported.</Text>}

                {variantData && variantData?.available_inventory <= 0 && (
                    <Text color="critical">Out of engraving stock.</Text>
                )}

                <div>SKU: {sku}</div>
                <div>{quantity} in cart</div>

                {item.properties && (
                    <div>
                        {item.properties.map((property) => (
                            <div>
                                <b style={{ display: 'block' }}>{property.name}:</b>
                                {property.value.startsWith('https://') &&
                                (property.value.includes('.png') || property.value.includes('.jpg')) ? (
                                    <img src={property.value} style={{ maxWidth: '100%' }} />
                                ) : (
                                    property.value
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </ResourceList.Item>
        );
    };

    return (
        <LegacyStack vertical={true} spacing="loose" distribution="fill">
            {loading && <Loading />}
            {(loading || cartContext.cartStatus == 'unavailable') && <Banner title={statusTitle} status={statusKind} />}

            {cartContext?.onlyAllowOneEngraving && (
                <Banner
                    title="Please note - only one item can be engraved per order. Please upgrade the POS app to the latest version."
                    status="warning"
                />
            )}

            {cartContext?.lineItems &&
                cartContext.lineItems.filter(
                    (item) => item.productId && item.productId != cartContext.engravingChargeProduct.id,
                ).length == 0 && <Banner title="No engravable items." status="warning" />}

            {cartContext?.productLineItems?.length && engravedProductItems?.length && productsData && (
                <LegacyStack vertical={true} spacing="tight" distribution="fill">
                    <Text variant="headingXl" as="h4">
                        Engraved Items
                    </Text>
                    <Card>
                        <ResourceList
                            resourceName={{ singular: 'product', plural: 'products' }}
                            items={engravedProductItems}
                            renderItem={DisplayedLineItem}
                        />
                    </Card>
                </LegacyStack>
            )}

            {cartContext?.productLineItems?.length && notEngravedProductItems?.length && productsData && (
                <LegacyStack vertical={true} spacing="tight" distribution="fill">
                    <Text variant="headingXl" as="h4">
                        Cart Items
                    </Text>
                    <Card>
                        <ResourceList
                            resourceName={{ singular: 'product', plural: 'products' }}
                            items={notEngravedProductItems}
                            renderItem={DisplayedLineItem}
                        />
                    </Card>
                </LegacyStack>
            )}
        </LegacyStack>
    );
}
